import { all, call, cancelled, put, takeLatest } from "redux-saga/effects";
import UserService from "../../services/UserService";
import {
  listSuccess,
  listFail,
  createSuccess,
  createFail,
  performActionSuccess,
  performActionFail,
  userDetailsSuccess,
  userDetailsFail,
  updateUserDetailSuccess,
  updateUserDetailFail,
  summarySuccess,
  summaryFail,
  downloadTemplateSuccess,
  downloadTemplateFail,
  userExportSuccess,
  userExportFail,
  userPendingAssignmentFail,
  userPendingAssignmentSuccess,
  markAssignmentAsCompletedSuccess,
  markAssignmentAsCompletedFail,
} from "../slices/usersSlice";
import { readErrorBody } from "../../helpers/ErrorMessage";

import {
  departmentFail,
  departmentSuccess,
  designationFail,
  designationSuccess,
  managerFail,
  managerSuccess,
} from "../slices/metaSlice";

function* list(data) {
  try {
    const ajax = yield call(UserService.list, data.payload);
    if (ajax.status === 200) {
      yield put(listSuccess(ajax.data));
    } else {
      yield put(
        listFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      listFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* adminUserList(data) {
  try {
    const ajax = yield call(UserService.adminUserlist, data.payload);
    if (ajax.status === 200) {
      yield put(listSuccess(ajax.data));
    } else {
      yield put(
        listFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      listFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* departments() {
  try {
    const ajax = yield call(UserService.departments);
    if (ajax.status === 200) {
      yield put(departmentSuccess(ajax.data));
    } else {
      yield put(
        departmentFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      departmentFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* designations() {
  try {
    const ajax = yield call(UserService.designations);
    if (ajax.status === 200) {
      yield put(designationSuccess(ajax.data));
    } else {
      yield put(
        designationFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      designationFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* managers() {
  try {
    const ajax = yield call(UserService.managers);
    if (ajax.status === 200) {
      yield put(managerSuccess(ajax.data));
    } else {
      yield put(
        managerFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      managerFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* create(data) {
  try {
    const ajax = yield call(UserService.create, data.payload);
    if (ajax.status === 200) {
      yield put(createSuccess(ajax.data));
    } else {
      yield put(
        createFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      createFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* bulkCreate(data) {
  try {
    const ajax = yield call(UserService.bulkCreate, data.payload);
    if (ajax.status === 200) {
      yield put(
        createSuccess({
          response: ajax.data,
          count: data.payload.learners.length,
        })
      );
    } else {
      yield put(
        createFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      createFail({
        status: error.status || 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function getAction(action) {
  switch (action) {
    case "delete":
      return UserService.delete;
    case "activate":
      return UserService.activate;
    case "deactivate":
      return UserService.deactivate;
    default:
      return null;
  }
}

// One user of a list action. A refusal is returned, not thrown, so the other
// selected users' requests are still waited for.
function* performOne(action, id) {
  try {
    return yield call(getAction(action), { id });
  } catch (error) {
    return { status: error?.response?.status || 400, error };
  }
}

export function* performAction(data) {
  try {
    let ids = [];
    if (data.payload?.ids) {
      ids = data.payload.ids;
    } else {
      ids.push(data.payload.id);
    }

    const results = yield all(
      ids.map((id) => call(performOne, data.payload.action, id))
    );
    const refused = results.find((result) => result?.status !== 200);

    if (!refused) {
      yield put(
        performActionSuccess({
          status: results[0].status,
          response: results[0].data,
          count: ids.length,
        })
      );
    } else {
      yield put(
        performActionFail({
          status: 400,
          response: "SOMETHING_ELSE_WRONG",
          ...(refused.error?.response?.data || {}),
        })
      );
    }
  } catch (error) {
    yield put(
      performActionFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* details(data) {
  try {
    const ajax = yield call(UserService.details, data.payload);
    if (ajax.status === 200) {
      yield put(userDetailsSuccess(ajax.data));
    } else {
      yield put(
        userDetailsFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      userDetailsFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* summary(data) {
  try {
    const ajax = yield call(UserService.summary, data.payload);
    if (ajax.status === 200) {
      yield put(summarySuccess(ajax.data));
    } else {
      yield put(
        summaryFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      summaryFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

export function* update(data) {
  try {
    // A details section is a tenant user's card; without one it is the super
    // admin's platform user form
    const ajax = yield call(
      data.payload?.detailType
        ? UserService.update
        : UserService.updatePlatformUser,
      data.payload
    );
    if (ajax.status === 200) {
      yield put(updateUserDetailSuccess(ajax.data));
    } else {
      yield put(
        updateUserDetailFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      updateUserDetailFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

export function* downloadTemplate() {
  try {
    const ajax = yield call(UserService.downloadTemplate);
    if (ajax.status === 200) {
      const url = window.URL.createObjectURL(new Blob([ajax.data]), {
        type: ajax.headers["content-type"],
      });
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      yield put(downloadTemplateSuccess());
    } else {
      yield put(
        downloadTemplateFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    // The template is requested as a file, so an error body arrives as a
    // Blob: it is read for the API's code, and the answer's own status is
    // kept. A 500 was reported as a 400, for which no message is shown, so
    // the admin was told nothing (A-BUG-1).
    const body = yield call(readErrorBody, error?.response?.data);
    yield put(
      downloadTemplateFail({
        response: "SOMETHING_ELSE_WRONG",
        ...body,
        status: error?.response?.status || body.status || 400,
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

export function* exportUsers(data) {
  try {
    const { fileType = "excel" } = data.payload;

    let extension = "xlsx";
    if (fileType === "csv") {
      extension = "csv";
    } else if (fileType === "pdf") {
      extension = "pdf";
    }

    const ajax = yield call(UserService.export, data.payload);
    if (ajax.status === 200) {
      const url = window.URL.createObjectURL(new Blob([ajax.data]), {
        type: ajax.headers["content-type"],
      });
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users.${extension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      yield put(userExportSuccess());
    } else {
      yield put(
        userExportFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    // The export is requested as a file, so an error body arrives as a Blob:
    // it is read for the API's code, and the answer's own status is kept.
    // A 500 was reported as a 400, for which no message is shown, so the
    // admin was told nothing (sibling of A-BUG-1, phase2-P-UI-2-review M3).
    const body = yield call(readErrorBody, error?.response?.data);
    yield put(
      userExportFail({
        response: "SOMETHING_ELSE_WRONG",
        ...body,
        status: error?.response?.status || body.status || 400,
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* pendingAssignments(data) {
  try {
    const ajax = yield call(UserService.pendingAssignments, data.payload);
    if (ajax.status === 200) {
      yield put(userPendingAssignmentSuccess(ajax.data));
    } else {
      yield put(
        userPendingAssignmentFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      userPendingAssignmentFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

function* markAssignmentAsCompleted(data) {
  try {
    const ajax = yield call(
      UserService.markUserAssignmentAsCompleted,
      data.payload
    );
    if (ajax.status === 200) {
      yield put(markAssignmentAsCompletedSuccess(ajax.data));
    } else {
      yield put(
        markAssignmentAsCompletedFail({
          status: ajax.status,
          errors: ajax.data,
        })
      );
    }
  } catch (error) {
    yield put(
      markAssignmentAsCompletedFail({
        status: 400,
        response: "SOMETHING_ELSE_WRONG",
        ...(error?.response?.data || {}),
      })
    );
  } finally {
    if (yield cancelled()) {
      // Handle saga cancellation (e.g., decrement the counter)
      yield put({ type: "cancel" });
    }
  }
}

export default function* userSaga() {
  yield all([
    takeLatest("users/listRequest", list),
    takeLatest("users/createRequest", create),
    takeLatest("users/bulkCreateRequest", bulkCreate),
    takeLatest("users/userDetailsRequest", details),
    takeLatest("users/performActionRequest", performAction),
    takeLatest("users/updateUserDetailRequest", update),
    takeLatest("meta/departmentRequest", departments),
    takeLatest("meta/designationRequest", designations),
    takeLatest("meta/managerRequest", managers),
    takeLatest("users/summaryRequest", summary),
    takeLatest("users/downloadTemplateRequest", downloadTemplate),
    takeLatest("users/adminListRequest", adminUserList),
    takeLatest("users/userExportRequest", exportUsers),
    takeLatest("users/userPendingAssignmentRequest", pendingAssignments),
    takeLatest(
      "users/markAssignmentAsCompletedRequest",
      markAssignmentAsCompleted
    ),
  ]);
}
